import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Calculator, Lock, Star, Trophy, Zap, Target, Brain, 
  Clock, ArrowLeft, Loader2, CheckCircle, Play, Gamepad2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';

const TimesTablesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { api, user } = useAuth();
  const isAdminPreview = location.pathname.includes('/admin/preview');
  
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTable, setSelectedTable] = useState(null);

  const getBasePath = () => isAdminPreview ? '/admin/preview/times-tables' : '/student/times-tables';

  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    try {
      if (user?.id && !isAdminPreview) {
        const response = await api.get(`/v2/times-tables/progress/${user.id}`);
        setProgress(response.data);
      } else {
        // Default progress for preview
        const tablesRes = await api.get('/v2/times-tables/all-tables');
        setProgress({
          overall: { tables_mastered: 0, total_tables: 12, current_table: 1, overall_accuracy: 0 },
          tables: tablesRes.data.tables.map((t, i) => ({
            ...t,
            unlocked: i === 0,
            mastered: false,
            accuracy: 0,
            stars: 0
          })),
          badges: []
        });
      }
    } catch (error) {
      console.error('Error fetching progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const tableColors = [
    'from-red-400 to-rose-500',
    'from-orange-400 to-amber-500', 
    'from-yellow-400 to-lime-500',
    'from-green-400 to-emerald-500',
    'from-teal-400 to-cyan-500',
    'from-blue-400 to-indigo-500',
    'from-indigo-400 to-violet-500',
    'from-purple-400 to-fuchsia-500',
    'from-pink-400 to-rose-500',
    'from-rose-400 to-red-500',
    'from-amber-400 to-orange-500',
    'from-emerald-400 to-teal-500'
  ];

  const renderStars = (count) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3].map((i) => (
          <Star 
            key={i} 
            className={`w-4 h-4 ${i <= count ? 'text-yellow-400 fill-yellow-400' : 'text-slate-300'}`} 
          />
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <StudentLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin text-purple-600" />
        </div>
      </StudentLayout>
    );
  }

  return (
    <StudentLayout>
      <div data-testid="times-tables-page" className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button 
          onClick={() => navigate(isAdminPreview ? '/admin' : '/student')}
          className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium mb-6"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Header */}
        <div className="student-card bg-gradient-to-r from-indigo-500 to-purple-600 text-white mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
                <Calculator className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold">Times Tables</h1>
                <p className="text-indigo-100">Master your multiplication from 1 to 12!</p>
              </div>
            </div>
            {progress && (
              <div className="text-right">
                <div className="text-4xl font-bold">{progress.overall.tables_mastered}/12</div>
                <div className="text-indigo-200 text-sm">Tables Mastered</div>
              </div>
            )}
          </div>
        </div>

        {/* Progress Overview */}
        {progress && (
          <div className="grid md:grid-cols-4 gap-4 mb-8">
            <div className="student-card text-center">
              <Trophy className="w-8 h-8 text-amber-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-slate-800">{progress.overall.tables_mastered}</div>
              <div className="text-sm text-slate-500">Mastered</div>
            </div>
            <div className="student-card text-center">
              <Target className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-slate-800">{progress.overall.overall_accuracy}%</div>
              <div className="text-sm text-slate-500">Accuracy</div>
            </div>
            <div className="student-card text-center">
              <Zap className="w-8 h-8 text-purple-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-slate-800">{progress.overall.current_table}</div>
              <div className="text-sm text-slate-500">Current Table</div>
            </div>
            <div className="student-card text-center">
              <Gamepad2 className="w-8 h-8 text-blue-500 mx-auto mb-2" />
              <div className="text-2xl font-bold text-slate-800">{progress.overall.total_games_played || 0}</div>
              <div className="text-sm text-slate-500">Games Played</div>
            </div>
          </div>
        )}

        {/* Badges Section */}
        {progress?.badges?.length > 0 && (
          <div className="student-card mb-6">
            <h2 className="font-bold text-slate-800 mb-3 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-amber-500" />
              Your Badges
            </h2>
            <div className="flex flex-wrap gap-3">
              {progress.badges.map((badge) => (
                <div 
                  key={badge.id}
                  className="flex items-center gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-full"
                  title={badge.description}
                >
                  <span className="text-xl">{badge.icon}</span>
                  <span className="font-medium text-amber-800 text-sm">{badge.name}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Times Tables Grid */}
        <h2 className="font-heading text-xl font-bold text-slate-800 mb-4">Choose a Times Table</h2>
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
          {progress?.tables?.map((table, index) => {
            const isUnlocked = table.unlocked;
            const isMastered = table.mastered;
            
            return (
              <div
                key={table.table_number}
                onClick={() => isUnlocked && setSelectedTable(table.table_number)}
                className={`relative rounded-2xl p-4 transition-all cursor-pointer ${
                  isUnlocked 
                    ? `bg-gradient-to-br ${tableColors[index]} text-white hover:scale-105 hover:shadow-lg` 
                    : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                } ${selectedTable === table.table_number ? 'ring-4 ring-purple-400 ring-offset-2' : ''}`}
                data-testid={`table-${table.table_number}`}
              >
                {!isUnlocked && (
                  <div className="absolute inset-0 flex items-center justify-center bg-slate-300/80 rounded-2xl">
                    <Lock className="w-8 h-8 text-slate-500" />
                  </div>
                )}
                {isMastered && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 bg-amber-400 rounded-full flex items-center justify-center shadow-lg">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                )}
                <div className="text-center">
                  <div className="text-3xl font-bold mb-1">{table.table_number}×</div>
                  <div className="text-xs opacity-80">
                    {isMastered ? 'Mastered!' : isUnlocked ? `${table.accuracy}%` : 'Locked'}
                  </div>
                  {isUnlocked && renderStars(table.stars || 0)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Selected Table Actions */}
        {selectedTable && (
          <div className="student-card border-2 border-purple-200 bg-purple-50" data-testid="table-actions">
            <h3 className="font-heading text-xl font-bold text-slate-800 mb-4">
              {selectedTable}× Table - Choose Activity
            </h3>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* Learn Section */}
              <div 
                onClick={() => navigate(`${getBasePath()}/learn/${selectedTable}`)}
                className="bg-white rounded-xl p-4 border-2 border-blue-200 hover:border-blue-400 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-3">
                  <Brain className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="font-bold text-slate-800">Learn</h4>
                <p className="text-sm text-slate-500">Visual arrays & skip counting</p>
              </div>

              {/* Speed Challenge */}
              <div 
                onClick={() => navigate(`${getBasePath()}/game/speed/${selectedTable}`)}
                className="bg-white rounded-xl p-4 border-2 border-amber-200 hover:border-amber-400 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="w-12 h-12 bg-amber-100 rounded-xl flex items-center justify-center mb-3">
                  <Zap className="w-6 h-6 text-amber-600" />
                </div>
                <h4 className="font-bold text-slate-800">Speed Challenge</h4>
                <p className="text-sm text-slate-500">60 seconds - how many can you get?</p>
              </div>

              {/* Target Practice */}
              <div 
                onClick={() => navigate(`${getBasePath()}/game/target/${selectedTable}`)}
                className="bg-white rounded-xl p-4 border-2 border-emerald-200 hover:border-emerald-400 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center mb-3">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
                <h4 className="font-bold text-slate-800">Target Practice</h4>
                <p className="text-sm text-slate-500">Shoot the correct answers!</p>
              </div>

              {/* Memory Match */}
              <div 
                onClick={() => navigate(`${getBasePath()}/game/memory/${selectedTable}`)}
                className="bg-white rounded-xl p-4 border-2 border-purple-200 hover:border-purple-400 cursor-pointer transition-all hover:shadow-md"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center mb-3">
                  <Brain className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="font-bold text-slate-800">Memory Match</h4>
                <p className="text-sm text-slate-500">Match equations with answers</p>
              </div>
            </div>

            <div className="mt-4 flex justify-center">
              <Button
                onClick={() => navigate(`${getBasePath()}/game/race/${selectedTable}`)}
                className="bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700"
              >
                <Clock className="w-4 h-4 mr-2" />
                Race Mode - Beat the Clock!
              </Button>
            </div>
          </div>
        )}

        {/* Info Section */}
        <div className="mt-8 student-card bg-gradient-to-r from-slate-50 to-slate-100">
          <h3 className="font-bold text-slate-800 mb-2">How to Master Your Times Tables</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-600">
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">1</div>
              <p><strong>Learn</strong> - Start with visual lessons to understand the concept</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-amber-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">2</div>
              <p><strong>Practice</strong> - Play games to build speed and accuracy</p>
            </div>
            <div className="flex items-start gap-2">
              <div className="w-6 h-6 bg-emerald-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">3</div>
              <p><strong>Master</strong> - Get 80%+ accuracy to unlock the next table!</p>
            </div>
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default TimesTablesPage;
